// src/pages/CouponStats.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Button, Stack, Grid, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, Alert, alpha, Divider, Tooltip, Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person'; // ✅ NEW
import ReceiptIcon from '@mui/icons-material/Receipt';
import SavingsIcon from '@mui/icons-material/Savings';
import TimerIcon from '@mui/icons-material/Timer';
import PublicIcon from '@mui/icons-material/Public';
import StoreIcon from '@mui/icons-material/Store';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { fetchCouponById, fetchCouponStats } from '../couponApi'; // ✅ FIXED PATH

export default function CouponStats() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [coupon, setCoupon] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [couponData, statsData] = await Promise.all([
        fetchCouponById(id),
        fetchCouponStats(id)
      ]);
      setCoupon(couponData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>;
  }

  if (!coupon || !stats) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/coupons')} sx={{ mb: 2, textTransform: 'none' }}>
          {t('Back') || 'Back'}
        </Button>
        <Alert severity="error">{t('CouponNotFound') || 'Coupon not found'}</Alert>
      </Box>
    );
  }

  const getStatusColor = () => {
    if (!coupon.IsActive) return 'default';
    const now = new Date();
    if (coupon.EndDate && new Date(coupon.EndDate) < now) return 'error';
    if (coupon.StartDate && new Date(coupon.StartDate) > now) return 'warning';
    return 'success';
  };

  const getStatusLabel = () => {
    if (!coupon.IsActive) return t('Inactive') || 'Inactive';
    const now = new Date();
    if (coupon.EndDate && new Date(coupon.EndDate) < now) return t('Expired') || 'Expired';
    if (coupon.StartDate && new Date(coupon.StartDate) > now) return t('Scheduled') || 'Scheduled';
    return t('Active') || 'Active';
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/coupons')} sx={{ textTransform: 'none' }}>
          {t('Back') || 'Back'}
        </Button>
        <Typography variant="h5" fontWeight={700}>
          {t('CouponStatistics') || 'Coupon Statistics'}
        </Typography>
      </Stack>

      {/* Coupon Info Card */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2}>
          {/* Left: Code, Type, Status, Value */}
          <Box>
            <Stack direction="row" spacing={2} alignItems="center" mb={1} flexWrap="wrap">
              <Chip
                label={coupon.Code}
                sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', px: 2, py: 2.5 }}
                color="primary"
              />
              <Chip
                icon={coupon.Type === 'discount' ? <LocalOfferIcon /> : <CardGiftcardIcon />}
                label={coupon.Type === 'discount' ? (t('Discount') || 'Discount') : (t('Voucher') || 'Voucher')}
                size="small"
                color={coupon.Type === 'discount' ? 'warning' : 'secondary'}
                variant="outlined"
              />
              <Chip label={getStatusLabel()} size="small" color={getStatusColor()} />
            </Stack>
            {coupon.Description && (
              <Typography variant="body2" color="text.secondary">{coupon.Description}</Typography>
            )}
            <Stack direction="row" spacing={1} mt={1}>
              <Typography variant="body2" fontWeight={600}>
                {t('Value') || 'Value'}: {coupon.FormattedValue}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({coupon.DiscountType === 'percentage' ? (t('Percentage') || 'Percentage') : (t('FixedAmount') || 'Fixed Amount')})
              </Typography>
            </Stack>
          </Box>

          {/* Right: Branches & Users */}
          <Stack spacing={2} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
            {/* Branch Scope */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {t('BranchScope') || 'Branch Scope'}:
              </Typography>
              {coupon.IsGlobal ? (
                <Chip icon={<PublicIcon />} label={t('AllBranches') || 'All Branches'} color="info" variant="outlined" size="small" />
              ) : (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {coupon.Branches.map(b => (
                    <Chip key={b.Id} icon={<StoreIcon />} label={b.Name} size="small" variant="outlined" />
                  ))}
                </Stack>
              )}
            </Box>

            {/* ✅ NEW: User Assignment */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {t('UserAssignment') || 'User Assignment'}:
              </Typography>
              {coupon.IsForAllUsers ? (
                <Chip icon={<PeopleIcon />} label={t('AllUsers') || 'All Users'} color="info" variant="outlined" size="small" />
              ) : (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {coupon.AssignedUsers.map(u => (
                    <Tooltip
                      key={u.Id}
                      title={[u.PhoneNumber, u.Email].filter(Boolean).join(' • ') || `User #${u.Id}`}
                    >
                      <Chip
                        avatar={
                          <Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem' }}>
                            {(u.Name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        }
                        label={u.Name || u.PhoneNumber || `#${u.Id}`}
                        size="small"
                        variant="outlined"
                        color="warning"
                      />
                    </Tooltip>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>

        {/* Date Range */}
        {(coupon.StartDate || coupon.EndDate) && (
          <Box mt={2} pt={2} sx={{ borderTop: '1px solid #e0e0e0' }}>
            <Stack direction="row" spacing={3}>
              {coupon.StartDate && (
                <Typography variant="body2" color="text.secondary">
                  {t('StartDate') || 'Start'}: <strong>{new Date(coupon.StartDate).toLocaleDateString()}</strong>
                </Typography>
              )}
              {coupon.EndDate && (
                <Typography variant="body2" color="text.secondary">
                  {t('EndDate') || 'End'}: <strong>{new Date(coupon.EndDate).toLocaleDateString()}</strong>
                </Typography>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Stats Summary Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          {
            label: t('TotalUses') || 'Total Uses',
            value: stats.totalUses,
            sub: `${stats.remainingUses} ${t('Remaining') || 'remaining'}`,
            color: '#1976d2',
            icon: <ReceiptIcon sx={{ fontSize: 36 }} />
          },
          {
            label: t('UniqueUsers') || 'Unique Users',
            value: stats.uniqueUsers,
            sub: t('CustomersUsed') || 'customers used this coupon',
            color: '#9c27b0',
            icon: <PeopleIcon sx={{ fontSize: 36 }} />
          },
          {
            label: t('TotalDiscountGiven') || 'Total Discount Given',
            value: stats.formattedTotalDiscountGiven || `${stats.totalDiscountGiven}`,
            sub: t('TotalSavings') || 'total savings given',
            color: '#2e7d32',
            icon: <SavingsIcon sx={{ fontSize: 36 }} />
          },
          {
            label: t('UsageRate') || 'Usage Rate',
            value: coupon.UsageLimit
              ? `${Math.round((stats.totalUses / coupon.UsageLimit) * 100)}%`
              : '∞',
            sub: coupon.UsageLimit
              ? `${stats.totalUses} / ${coupon.UsageLimit}`
              : (t('Unlimited') || 'Unlimited'),
            color: '#ed6c02',
            icon: <TimerIcon sx={{ fontSize: 36 }} />
          }
        ].map((card, idx) => (
          <Grid item xs={6} md={3} key={idx}>
            <Card elevation={0} sx={{ border: `1px solid ${alpha(card.color, 0.2)}`, borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                    <Typography variant="h4" fontWeight={700} color={card.color}>{card.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{card.sub}</Typography>
                  </Box>
                  <Box sx={{ color: alpha(card.color, 0.3) }}>{card.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ✅ NEW: Assigned Users Detail Card (only when specific users are assigned) */}
      {coupon.AssignedUsers && coupon.AssignedUsers.length > 0 && (
        <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon color="warning" />
              <Typography variant="subtitle1" fontWeight={700}>
                {t('AssignedUsers') || 'Assigned Users'} ({coupon.AssignedUsers.length})
              </Typography>
            </Stack>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('UserName') || 'Name'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('PhoneNumber') || 'Phone'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('Email') || 'Email'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coupon.AssignedUsers.map((user, idx) => (
                  <TableRow key={user.Id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 26, height: 26, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                          {(user.Name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {user.Name || `User #${user.Id}`}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.PhoneNumber || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.Email || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Usage History Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('UsageHistory') || 'Usage History'}
          </Typography>
        </Box>

        {stats.usages.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 50, color: alpha('#000', 0.15), mb: 1 }} />
            <Typography color="text.secondary">
              {t('NoUsageYet') || 'This coupon has not been used yet.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('OrderId') || 'Order ID'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('UserId') || 'User ID'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('BranchId') || 'Branch ID'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('DiscountApplied') || 'Discount Applied'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('Date') || 'Date'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.usages.map((usage, idx) => (
                  <TableRow key={usage.Id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Chip
                        label={usage.OrderId}
                        size="small"
                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                        onClick={() => navigate('/admin/orders')}
                        clickable
                      />
                    </TableCell>
                    <TableCell>
                      {usage.UserId ? (
                        <Chip label={`#${usage.UserId}`} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">{t('Guest') || 'Guest'}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {usage.BranchId ? (
                        <Chip label={`#${usage.BranchId}`} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} color="success.main">
                        -{usage.FormattedDiscountApplied || usage.DiscountApplied}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(usage.UsedAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(usage.UsedAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}